import React from "react";
import { Moon, Sun, ArrowUpRight } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import ExperienceItem from "../components/ExperienceItem";
import CustomCursor from "components/custom-cursor";
import ProjectPreview from "components/ProjectPreview";
import SourceWelcome from "components/SourceWelcome";
import GithubActivityChart from "components/GithubActivityChart";
import { useViewMode } from "../ViewMode";
import SceneMenu from "../immersive/components/SceneMenu";
import "./Portfolio.css";
import "./classic.css";

const CURRENTLY = [
  {
    number: "01",
    title: "frontend engineer @ capitalsage",
    description:
      "building scalable, high-performance UI components using Next.js and TailwindCSS. leading frontend team and optimizing builds for rapid deployment.",
    meta: "since may 2025",
    showArrow: false,
  },
  {
    number: "02",
    title: "postgraduate student @ babcock university",
    description: "studying for a master's degree in computer science.",
    meta: "since january 2026",
  },
  {
    number: "03",
    title: "freelance developer",
    description: (
      <>
        continuously shipping production-ready applications. focus on React,
        Next.js, and modern web technologies. check out my work on{" "}
        <a
          href="https://github.com/EarlPappi"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-link"
        >
          github <ArrowUpRight size={12} className="inline-arrow" />
        </a>
      </>
    ),
  },
  {
    number: "04",
    title: "amateur table tennis player",
    description:
      "playing table tennis for fun and fitness, actively competing in local tournaments.",
    meta: "since 2021",
  },
];

const PAST_LIFE = [
  {
    number: "01",
    title: "fullstack developer @ pureworker",
    description:
      "led the frontend team building responsive interfaces and a reusable component library. drove 22% retention lift via mobile optimization and 84% organic traffic growth through SEO.",
    meta: "(march 2024 - september 2024)",
    showArrow: true,
  },
  {
    number: "02",
    title: "frontend engineer @ african founders",
    description:
      "led frontend team shipping responsive screens and shared components. boosted user retention by 19% with mobile-first optimization and grew organic traffic 30% via end-to-end SEO.",
    meta: "(september 2022 - january 2024)",
    showArrow: true,
  },
  {
    number: "03",
    title: "fullstack developer @ forenxy",
    description:
      "built crypto exchange platform with real-time trading UI, order books, and wallet integrations. managed frontend team and conducted code reviews.",
    meta: "(february 2021 - january 2022) contract",
  },
  {
    number: "04",
    title: "education @ university of ibadan (bsc. agronomy)",
    description:
      "bachelor of science degree. balanced academic excellence with professional development in web technologies.",
    meta: "(2016-2023) - ibadan, nigeria",
  },
  {
    number: "05",
    title: "skills & expertise",
    description:
      "HTML, CSS3/SCSS, JavaScript (ES6), TypeScript, Python. React, Next.js, Node.js, Redux. TailwindCSS, Bootstrap, Material UI. Git, GitHub, Jira, Slack, wordpress, framer, and more.",
    descriptionClassName: "experience-description--lowercase",
  },
];

const PROJECTS = [
  {
    title: "nearestkin",
    shot: "/shots/nearestkin.webp",
    description:
      "keeps families close to someone in prison or jail. built an ivr the person inside reaches from an ordinary facility phone (messages, live calls and an ai assistant, all by voice), a web app and react native mobile app for family outside, an admin console, and stripe billing.",
    url: "https://nearestkin.com/",
  },
  {
    title: "zaltev",
    shot: "/shots/zaltev.webp",
    description:
      "multi-tenant trading platform. each user's strategy runs in its own isolated worker process, with an async fastapi backend, react dashboard, admin console, two telegram bots and crypto billing.",
    url: "https://zaltev.com/",
  },
  {
    title: "weonline.studio",
    shot: "/shots/weonline.webp",
    description:
      "site and brand for a senior-led software studio — service catalogue, case studies and the enquiry pipeline behind its five-day MVP engagement. built end to end.",
    url: "https://weonline.studio/",
  },
  {
    title: "medflow",
    shot: "/shots/medflow.webp",
    description:
      "multi-tenant hospital management software running several facilities off one deployment. patient records, appointments and staff scheduling, plus billing with online payment collection, insurance claims, pharmacy and stock control, lab results, and role-based access down to the department.",
    url: "https://medflow.earlpappi.com/",
  },
  {
    title: "kindssecurity",
    shot: "/shots/kindssecurity.webp",
    description:
      "email security solution, worked on both the admin portal, learners portal and the marketing site",
    url: "https://kindssecurity.com/",
  },
  {
    title: "t-hrms",
    shot: "/shots/t-hrms.webp",
    description:
      "a human resource management system built with nextjs and nodejs. username: admin@thrm.com, password: Admin@123456.",
    url: "https://t-hrm.vercel.app/",
  },
  {
    title: "blaze.money (reimagined)",
    shot: "/shots/blaze.webp",
    description:
      "rebuilt the existing blaze.money site from scratch — improved UI, performance, and overall experience while preserving the brand.",
    url: "https://blaze-main.vercel.app/",
  },
  {
    title: "j-em (now rivages)",
    shot: "/shots/j-em.webp",
    description:
      "official website for j-em, a canadian SME, handled seo as well",
    url: "https://j-em.ca/",
  },
];

const Portfolio = () => {
  const { isDark, toggleTheme } = useTheme();
  const { enterWindows, enterImmersive, scene } = useViewMode();

  return (
    <>
      <CustomCursor />
      <ProjectPreview />
      <SourceWelcome />
      <main className="portfolio-container" role="main">
        {/* Header actions: resume download + theme toggle  --- static bar too yunno*/}
        <header className="header-actions" aria-label="Page actions">
          <a
            id="download-resume"
            href={`${process.env.PUBLIC_URL || ""}/miracle_aponjolosun.pdf`}
            download="miracle_aponjolosun.pdf"
            className="download-resume"
          >
            resume
          </a>
          <SceneMenu
            className="mode-switch--immersive"
            activeScene={scene}
            onSelect={enterImmersive}
          >
            <span className="mode-switch__orb" aria-hidden="true" />
            <span>immersive</span>
          </SceneMenu>
          <button
            className="mode-switch"
            onClick={enterWindows}
            title="Open the Windows 11 version of this site"
          >
            <span className="mode-switch__logo" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
            </span>
            <span>windows 11</span>
          </button>
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </header>

        <div className="portfolio-grid">
          {/* Left Column - Profile */}
          <div
            className="profile-section"
            aria-label="About Miracle Aponjolosun"
          >
            <h1 className="name">Miracle Aponjolosun</h1>
            <p className="tagline">earlpappi</p>

            <p className="bio">
              fullstack developer with 5 years of experience building scalable,
              end-to-end web applications. strong focus on React, Next.js, and
              Node.js from UI to production APIs.
            </p>

            <div className="location">lagos / nigeria</div>

            <div className="contact-links">
              <a
                id="contact-email"
                href="mailto:maponjolosun@gmail.com"
                className="contact-link"
              >
                maponjolosun@gmail.com
              </a>
              <a href="tel:+2348145406839" className="contact-link">
                +234(814)-540-6839
              </a>
            </div>

            <div className="profile-image-wrapper">
              <img
                src="https://avatars.githubusercontent.com/u/94018014?v=4"
                alt="Miracle Aponjolosun"
                className="profile-image"
              />
            </div>

            <div className="social-section">
              <h3 className="section-title">Elsewhere</h3>
              <div className="social-links">
                <a
                  href="https://twitter.com/earl_pappi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  x <ArrowUpRight size={14} className="arrow-icon" />
                </a>
                <a
                  href="https://www.linkedin.com/in/miracle-aponjolosun-793519182/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  linkedin <ArrowUpRight size={14} className="arrow-icon" />
                </a>
                <a
                  href="https://github.com/EarlPappi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  github <ArrowUpRight size={14} className="arrow-icon" />
                </a>
              </div>
            </div>
          </div>

          {/* Middle Column - Currently */}
          <section
            className="currently-section"
            aria-label="Current work and activities"
          >
            <h2 className="column-header">CURRENTLY</h2>
            {CURRENTLY.map((item) => (
              <ExperienceItem key={item.number} {...item} />
            ))}
          </section>

          {/* Right Column - Past Life */}
          <section
            className="past-section"
            aria-label="Experience and background"
          >
            <h2 className="column-header">PAST LIFE</h2>
            {PAST_LIFE.map((item) => (
              <ExperienceItem key={item.number} {...item} />
            ))}
          </section>
        </div>

        {/* Projects Section */}
        <section
          id="projects-section"
          className="projects-section"
          aria-label="Projects by Miracle Aponjolosun"
        >
          <h2 className="column-header">PROJECTS</h2>
          <div className="projects-list">
            {PROJECTS.map((project, index) => (
              <div
                key={index}
                className="project-item"
                data-preview={`${process.env.PUBLIC_URL || ""}${project.shot}`}
              >
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link"
                >
                  {project.title}
                  <ArrowUpRight size={14} className="project-link-icon" />
                </a>
                <p className="project-description">{project.description}</p>
              </div>
            ))}
          </div>
        </section>

        <GithubActivityChart username="EarlPappi" />

        {/* Footer */}
        <footer className="portfolio-footer">
          © {new Date().getFullYear()} Miracle Aponjolosun.
        </footer>
      </main>
    </>
  );
};

export default Portfolio;
